import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { vaultSyncConfigurationService } from '@/services/vault-sync-configuration.service';
import {
  CreateVaultSyncConfigurationDto,
  UpdateVaultSyncConfigurationDto,
  ManualSyncRequest,
} from '@/types/vault-sync-configuration.types';

// Query Keys
export const vaultSyncConfigKeys = {
  all: ['vaultSyncConfig'] as const,
  folder: (folderUuid: string) =>
    [...vaultSyncConfigKeys.all, folderUuid] as const,
};

/**
 * Hook to get vault sync configuration for a folder
 */
export const useVaultSyncConfiguration = (folderUuid: string) => {
  return useQuery({
    queryKey: vaultSyncConfigKeys.folder(folderUuid),
    queryFn: () => vaultSyncConfigurationService.getConfiguration(folderUuid),
    enabled: !!folderUuid,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to create or update vault sync configuration
 */
export const useSaveVaultSyncConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateVaultSyncConfigurationDto) =>
      vaultSyncConfigurationService.createOrUpdate(dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: vaultSyncConfigKeys.folder(variables.folderUuid),
      });
      toast.success('Configuration de synchronisation enregistrée');
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Erreur lors de l'enregistrement de la configuration";
      toast.error(message);
    },
  });
};

/**
 * Hook to update vault sync configuration
 */
export const useUpdateVaultSyncConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      folderUuid,
      dto,
    }: {
      folderUuid: string;
      dto: UpdateVaultSyncConfigurationDto;
    }) => vaultSyncConfigurationService.update(folderUuid, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: vaultSyncConfigKeys.folder(variables.folderUuid),
      });
      toast.success('Configuration de synchronisation mise à jour');
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Erreur lors de la mise à jour de la configuration';
      toast.error(message);
    },
  });
};

/**
 * Hook to delete vault sync configuration
 */
export const useDeleteVaultSyncConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (folderUuid: string) =>
      vaultSyncConfigurationService.delete(folderUuid),
    onSuccess: (_data, folderUuid) => {
      queryClient.invalidateQueries({
        queryKey: vaultSyncConfigKeys.folder(folderUuid),
      });
      toast.success('Configuration de synchronisation supprimée');
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Erreur lors de la suppression de la configuration';
      toast.error(message);
    },
  });
};

/**
 * Hook to execute manual vault sync
 */
export const useManualVaultSync = () => {
  return useMutation({
    mutationFn: (request: ManualSyncRequest) =>
      vaultSyncConfigurationService.executeManualSync(request),
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Erreur lors de la synchronisation';
      toast.error(message);
    },
  });
};

/**
 * Hook to upload a local file to an employee's vault
 */
export const useLocalVaultUpload = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      folderUuid: string;
      employeeUuid: string;
      file: File;
      documentType: string;
      year: number;
      month: number;
      employeeNumber?: string;
    }) => vaultSyncConfigurationService.uploadLocalFile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vaults'] });
      toast.success('Document téléversé avec succès');
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Erreur lors du téléversement du document';
      toast.error(message);
    },
  });
};

/**
 * Hook to get available periods for manual sync
 */
export const useAvailablePeriods = (folderUuid: string) => {
  return useQuery({
    queryKey: ['vaultSyncPeriods', folderUuid] as const,
    queryFn: () =>
      vaultSyncConfigurationService.getAvailablePeriods(folderUuid),
    enabled: !!folderUuid,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};
